import { NgIf } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseHorizontalNavigationComponent, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FooterType01Component } from 'app/layout/common/footer/footer-type01/footer-type01.component';
import { FooterType02Component } from 'app/layout/common/footer/footer-type02/footer-type02.component';
import { FooterComponent } from 'app/layout/common/footer/footer.component';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { TFMService } from 'app/mock-api/tfm.service';
import { Subject, takeUntil } from 'rxjs';
import { DestinationListType01Component } from 'app/modules/client/destination-list/destination-list-type01/destination-list-type01.component';
import { TFMCartService } from 'app/mock-api/tfm-cart.service';
import { CartComponent } from 'app/layout/common/cart/cart.component';
import { MatSidenavModule } from '@angular/material/sidenav';

@Component({
    selector: 'material-layout',
    templateUrl: './material.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [RouterModule, FuseLoadingBarComponent, NgIf, FuseVerticalNavigationComponent, MatButtonModule, MatIconModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent, ShortcutsComponent, MessagesComponent,
        NotificationsComponent, UserComponent, FuseHorizontalNavigationComponent, RouterOutlet, FooterComponent, FooterType01Component, DestinationListType01Component, FooterType02Component, MatSidenavModule, CartComponent],
})
export class MaterialLayoutComponent implements OnInit, OnDestroy {
    public view: any = {
        isClient: false,
        data: {
            cart: []
        },
        drawers: {
            review: {}
        }
    };
    public drawerMode: 'over' | 'side' | 'push' = 'push';

    isScreenSmall: boolean;
    navigation: Navigation;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private tfmService: TFMService,
        private _tfmCartService: TFMCartService,
        private _cd: ChangeDetectorRef
    ) {
        this.view.tripChange$ = this._tfmCartService.tripChange.subscribe(res => {
            this.view.trip = res;
        });

        this.view.cartChange$ = this._tfmCartService.cartChange.subscribe(res => {
            this.view.data.cart = res;
        });
    }

    sendEmail(email: string) {
        const subject = `Website Contact Request`;
        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}`;
        window.location.href = mailtoLink;
    }

    /** Whether the current route is the search result screen (cart should show limited columns). */
    isSearchResultRoute(): boolean {
        return this._router.url.includes('search-results');
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.view.isClient = this.tfmService.view.isClient;

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                if (this.view.isClient) {
                    this.tfmService.getDestinations().then((res: any) => {
                        const grouped: Record<string, any[]> = {};
                        res.forEach(x => {
                            const continent = x.parent?.name || 'Other';
                            if (!grouped[continent]) grouped[continent] = [];
                            grouped[continent].push({
                                id: x.id,
                                title: x.name,
                                type: "basic",
                                icon: "",
                                link: `/destination/${x.id}/${x.seo.alias}`
                            });
                        });
                        const destinations = Object.keys(grouped).sort().map(continent => ({
                            id: continent.toLowerCase().replace(/\s+/g, '-'),
                            title: continent,
                            subtitle: "",
                            type: "collapsable" as const,
                            icon: "",
                            children: grouped[continent]
                        }));
                        this.view.pages = res;

                        this.tfmService.getPages().then(pagesRes => {
                            this.view.menuItems = pagesRes;

                            const dynamicNavItems = this.view.menuItems.content
                                .filter(item => item.section !== 'privacy' && item.section !== 'terms' && item.section !== 'specials')
                                .map(item => ({
                                    id: item.name.toLowerCase().replace(/\s+/g, '-'),
                                    title: item.name,
                                    subtitle: "",
                                    type: "basic",
                                    icon: "",
                                    children: [],
                                    link: "/" + item.name.toLowerCase().replace(/\s+/g, '-')
                                }));

                            this.tfmService.getLandingPages().then(lpRes => {
                                this.view.landingPages = lpRes;

                                const specials = (lpRes.content || []).filter(lp => lp.isPinned === true).map((lp: any) => ({
                                    id: lp.id,
                                    title: lp.name,
                                    type: "basic",
                                    icon: "",
                                    link: `/destination/${lp.id}/${lp.seo.alias}`
                                }));

                                navigation.horizontal = [];

                                if (this.view.site?.profile?.heading) {
                                    switch (this.view.site.profile.heading.menu) {
                                        case 1:
                                            navigation.horizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "destinations", title: "Destinations", subtitle: "", type: "group", icon: "feather:chevron-down", children: destinations },
                                                {
                                                    id: "sports-packages", title: "Simply Events", subtitle: "", type: "group", icon: "feather:chevron-down", children: [{
                                                        id: "tickets",
                                                        title: "Tickets",
                                                        type: "basic",
                                                        link: "/packages/events"
                                                    },
                                                    {
                                                        id: "packages",
                                                        title: "Packages",
                                                        type: "basic",
                                                        link: "/packages/sports"
                                                    }
                                                    ],
                                                },
                                                { id: "promotional", title: "Specials", subtitle: "", type: "basic", icon: "", children: [], link: "/promotional" },
                                                { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                                { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact-us" },
                                                // { id: "promotions", title: "Promotions", subtitle: "", type: "group", icon: "feather:chevron-down", children: specials },
                                            );
                                            break;

                                        case 2:
                                            navigation.horizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                                { id: "corporate-travel", title: "Corporate Travel", subtitle: "", type: "basic", icon: "", children: [], link: "/corporate-travel" },
                                                { id: "leisure-travel", title: "Leisure Travel", subtitle: "", type: "basic", icon: "", children: [], link: "/leisure-travel" },
                                                { id: "branches", title: "Members", subtitle: "", type: "basic", icon: "", children: [], link: "/branches" },
                                                { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact" },
                                            );
                                            break;

                                        case 3:
                                            navigation.horizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "about", title: "About", subtitle: "", type: "basic", icon: "", children: [], link: "/about-us" },
                                                { id: "world-cruises", title: "World Cruises", subtitle: "", type: "basic", icon: "", children: [], link: "/world-cruises" },
                                                { id: "cruise-destinations", title: "Cruise Destinations", subtitle: "", type: "basic", icon: "", children: [], link: "/cruise-destinations" },
                                                { id: "gallery", title: "Gallery", subtitle: "", type: "basic", icon: "", children: [], link: "/gallery" },
                                                { id: "contact", title: "Contact", subtitle: "", type: "basic", icon: "", children: [], link: "/contact" },
                                            );
                                            break;

                                        case 4:
                                            navigation.horizontal.push(
                                                { id: "home", title: "Home", subtitle: "", type: "basic", icon: "", children: [], link: "/" },
                                                { id: "destinations", title: "Destinations", subtitle: "", type: "group", icon: "feather:chevron-down", children: destinations },
                                                ...dynamicNavItems,
                                                { id: "specials", title: "Specials", subtitle: "", type: "group", icon: "feather:chevron-down", children: specials }
                                            );
                                            break;
                                    }
                                }

                                this.navigation = navigation;
                                // console.log('Filtered Dynamic Nav Items:', dynamicNavItems);
                                // console.log('Landing Pages:', this.view.landingPages);
                                // console.log('Nav Items:', this.view.menuItems);
                            });
                        });
                    });
                } else {
                    this.navigation = navigation;
                }

                // this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
        this.tfmService.getSite().then(res => {
            this.view.site = res;
            this.view.site.profile = this.tfmService.getSiteProfile(this.view.site.profileID);

        });
        this.view.itinGuidChange$ = this.tfmService.itinGuidChange.subscribe(res => {
            this.view.itinGuid = this.tfmService.getLocalItinGuid();

            //this._changeDetectorRef.detectChanges();
        });

        this.view.itinGuid = this.tfmService.getLocalItinGuid();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.view.tripChange$.unsubscribe();
        this.view.cartChange$.unsubscribe();

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }
    toggleNavigationB2C(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    viewChanged(options) {
        this.view.drawerOptions = options;
    }
    quoteCreated(quoteGuid) {
        if (quoteGuid)
            this._router.navigate(['/checkout', quoteGuid]);
    }
}
